import { useId, useState, type FormEvent } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useBlockedCarriers,
  useRemoveBlockedCarrier,
  useSaveBlockedCarrier,
  type BlockedCarrier,
  type BlockedCarrierWrite,
} from '@/api/blocked-carriers'
import {
  InlineFieldGroup,
  InlineFieldInput,
  type InlineGroupField,
} from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getApiErrorMessage } from '@/lib/api-error'
import { parseUTCDate } from '@/lib/utils'

type NameMatch = BlockedCarrierWrite['name_match']

const MATCH_OPTIONS = [
  { value: 'substring', label: 'Name contains' },
  { value: 'exact', label: 'Exact name' },
] as const satisfies ReadonlyArray<{ value: NameMatch; label: string }>

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

function lastShipped(carrier: BlockedCarrier): string {
  if (!carrier.shipment_count || !carrier.last_shipment_at) return 'None'
  const date = parseUTCDate(carrier.last_shipment_at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${carrier.shipment_count} · last ${date}`
}

function CarrierMatches({ carrier }: { carrier: BlockedCarrier }) {
  const names = [carrier.name, ...carrier.other_names]
  return (
    <span className="block space-y-1.5">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">
          {carrier.name_match === 'exact' ? 'Exact name' : 'Name contains'}
        </span>
        {names.map((name) => (
          <Badge key={name} variant="secondary" className="font-normal">
            {name}
          </Badge>
        ))}
      </span>
      {carrier.scac_codes.length > 0 && (
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">SCAC</span>
          {carrier.scac_codes.map((scac) => (
            <Badge key={scac} variant="outline" className="font-mono font-normal">
              {scac}
            </Badge>
          ))}
        </span>
      )}
    </span>
  )
}

type CarrierKey = 'name' | 'name_match' | 'other_names' | 'scac_codes'

const CARRIER_FIELDS: ReadonlyArray<InlineGroupField<CarrierKey>> = [
  { key: 'name', label: 'Carrier', required: true, className: 'col-span-6 sm:col-span-3' },
  {
    key: 'name_match',
    label: 'Match',
    kind: 'select',
    options: MATCH_OPTIONS,
    className: 'col-span-6 sm:col-span-3',
  },
  { key: 'other_names', label: 'Other names', className: 'col-span-6 sm:col-span-3' },
  { key: 'scac_codes', label: 'SCAC codes', className: 'col-span-6 sm:col-span-3' },
]

/** A blocked carrier's names and SCAC codes, edited in place (USA-1580). */
function BlockedCarrierEditor({
  carrier,
  editing,
  onEditingChange,
}: {
  carrier: BlockedCarrier
  editing: boolean
  onEditingChange: (editing: boolean) => void
}) {
  const save = useSaveBlockedCarrier()
  const [error, setError] = useState<string | null>(null)

  function submit(next: Record<CarrierKey, string>) {
    setError(null)
    save.mutate(
      {
        uuid: carrier.uuid,
        body: {
          name: next.name,
          name_match: next.name_match as NameMatch,
          other_names: splitList(next.other_names),
          scac_codes: splitList(next.scac_codes),
        },
      },
      {
        onSuccess: (saved) => {
          toast.success(`${saved.name} updated`)
          onEditingChange(false)
        },
        onError: (saveError) => setError(getApiErrorMessage(saveError, 'Could not save')),
      },
    )
  }

  return (
    <div className="space-y-2">
      <InlineFieldGroup
        label={`Matches for ${carrier.name}`}
        fields={CARRIER_FIELDS}
        values={{
          name: carrier.name,
          name_match: carrier.name_match,
          other_names: carrier.other_names.join(', '),
          scac_codes: carrier.scac_codes.join(', '),
        }}
        labelled
        pending={save.isPending}
        editing={editing}
        onEditingChange={(next) => {
          if (!next) setError(null)
          onEditingChange(next)
        }}
        onSave={submit}
      >
        <CarrierMatches carrier={carrier} />
      </InlineFieldGroup>
      {editing && error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}

function BlockedCarrierForm({ onDone }: { onDone: () => void }) {
  const save = useSaveBlockedCarrier()
  const [name, setName] = useState('')
  const [nameMatch, setNameMatch] = useState<NameMatch>('substring')
  const [otherNames, setOtherNames] = useState('')
  const [scacCodes, setScacCodes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const idPrefix = useId()

  function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    save.mutate(
      {
        body: {
          name,
          name_match: nameMatch,
          other_names: splitList(otherNames),
          scac_codes: splitList(scacCodes),
        },
      },
      {
        onSuccess: (saved) => {
          toast.success(`${saved.name} blocked`)
          onDone()
        },
        onError: (saveError) => setError(getApiErrorMessage(saveError, 'Could not save')),
      },
    )
  }

  return (
    <form onSubmit={submit} className="space-y-4 rounded-[10px] border bg-card p-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-name`}>Carrier</Label>
          <InlineFieldInput
            id={`${idPrefix}-name`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <span className="block text-sm font-medium">Match</span>
          <ReviewChoiceGroup
            label="Match"
            value={nameMatch}
            options={MATCH_OPTIONS}
            onChange={setNameMatch}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-other-names`}>Other names</Label>
          <InlineFieldInput
            id={`${idPrefix}-other-names`}
            value={otherNames}
            onChange={(event) => setOtherNames(event.target.value)}
            placeholder="TForce, T Force"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-scac`}>SCAC codes</Label>
          <InlineFieldInput
            id={`${idPrefix}-scac`}
            value={scacCodes}
            onChange={(event) => setScacCodes(event.target.value)}
            placeholder="UPGF"
            className="font-mono uppercase placeholder:normal-case"
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <LoadingButton type="submit" size="sm" loading={save.isPending} loadingText="Saving...">
          Block carrier
        </LoadingButton>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function RowActions({
  carrier,
  onEdit,
  onRemove,
  removing,
}: {
  carrier: BlockedCarrier
  onEdit: () => void
  onRemove: () => void
  removing: boolean
}) {
  const [confirming, setConfirming] = useState(false)

  if (confirming) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span className="text-sm">Unblock?</span>
        <LoadingButton size="sm" variant="destructive" loading={removing} onClick={onRemove}>
          Unblock
        </LoadingButton>
        <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
          Keep
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Button variant="ghost" size="icon" aria-label={`Edit ${carrier.name}`} onClick={onEdit}>
        <Pencil />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Unblock ${carrier.name}`}
        onClick={() => setConfirming(true)}
      >
        <Trash2 />
      </Button>
    </div>
  )
}

export function BlockedCarriersPanel() {
  const { data: carriers, isLoading, isError, error } = useBlockedCarriers()
  const remove = useRemoveBlockedCarrier()
  const [editing, setEditing] = useState<string | null>(null)

  // The row leaves the list as soon as Unblock is pressed, so the panel (not the row) reports back.
  function unblock(carrier: BlockedCarrier) {
    remove.mutate(carrier.uuid, {
      onSuccess: () => toast.success(`${carrier.name} unblocked`),
      onError: (error) =>
        toast.error(getApiErrorMessage(error, `Could not unblock ${carrier.name}`)),
    })
  }

  const adding = editing === 'new'

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing('new')} disabled={adding}>
          <Plus /> Block a carrier
        </Button>
      </div>

      {adding && <BlockedCarrierForm onDone={() => setEditing(null)} />}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </div>
      ) : isError ? (
        <p role="alert" className="py-10 text-center text-sm text-destructive">
          {getApiErrorMessage(error, 'Blocked carriers could not be loaded')}
        </p>
      ) : !carriers?.length ? (
        <p className="py-10 text-center text-sm text-muted-foreground">No blocked carriers</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Matches</TableHead>
                  <TableHead>Our shipments</TableHead>
                  <TableHead className="w-[1%]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carriers.map((carrier) =>
                  editing === carrier.uuid ? (
                    <TableRow key={carrier.uuid} className="hover:bg-transparent">
                      <TableCell colSpan={4} className="whitespace-normal">
                        <BlockedCarrierEditor
                          carrier={carrier}
                          editing
                          onEditingChange={(next) => setEditing(next ? carrier.uuid : null)}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={carrier.uuid}>
                      <TableCell className="align-top font-medium">{carrier.name}</TableCell>
                      <TableCell className="whitespace-normal align-top">
                        <BlockedCarrierEditor
                          carrier={carrier}
                          editing={false}
                          onEditingChange={(next) => setEditing(next ? carrier.uuid : null)}
                        />
                      </TableCell>
                      <TableCell className="align-top tabular-nums text-muted-foreground">
                        {lastShipped(carrier)}
                      </TableCell>
                      <TableCell className="align-top">
                        <RowActions
                          carrier={carrier}
                          onEdit={() => setEditing(carrier.uuid)}
                          onRemove={() => unblock(carrier)}
                          removing={remove.isPending && remove.variables === carrier.uuid}
                        />
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile list */}
          <div className="space-y-2 lg:hidden">
            {carriers.map((carrier) => (
              <div key={carrier.uuid} className="space-y-2 rounded-lg border bg-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium">{carrier.name}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      Our shipments: {lastShipped(carrier)}
                    </p>
                  </div>
                  {editing !== carrier.uuid && (
                    <RowActions
                      carrier={carrier}
                      onEdit={() => setEditing(carrier.uuid)}
                      onRemove={() => unblock(carrier)}
                      removing={remove.isPending && remove.variables === carrier.uuid}
                    />
                  )}
                </div>
                <BlockedCarrierEditor
                  carrier={carrier}
                  editing={editing === carrier.uuid}
                  onEditingChange={(next) => setEditing(next ? carrier.uuid : null)}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
